import Link from "next/link";

export const TRIAL_PATH = "https://app.trackforce.io/3/Professional/signup?trial=true";
export const DEMO_PATH = "/contact";

export const ArrowIcon = ({ size = 17 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M5 12h13M12 5l7 7-7 7" />
  </svg>
);

export function CtaButtons() {
  return (
    <div className="cta-row">
      <Link href={TRIAL_PATH} className="btn btn-primary">
        Start Free Trial
        <ArrowIcon />
      </Link>
      <Link href={DEMO_PATH} className="btn btn-onDark">
        Book a Demo
      </Link>
    </div>
  );
}

export function TrialMicro() {
  return (
    <p className="micro mt-2!">
      Free 30-day trial <span className="sep">|</span> No credit card required{" "}
      <span className="sep">|</span> Cancel anytime
    </p>
  );
}

export function TrustpilotBadge() {
  return (
    <div className="tp">
      <b>Excellent</b>
      <span className="stars" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <span className="st" key={i}>
            <svg viewBox="0 0 24 24" fill="#fff">
              <path d="M12 2l3 7h7l-5.5 4.5L18.5 21 12 16.8 5.5 21l2-7.5L2 9h7z" />
            </svg>
          </span>
        ))}
      </span>
      <span>Trustpilot, based on 456 reviews</span>
    </div>
  );
}

const CUSTOMER_LOGOS = [
  { src: "/seo-landing/akij-air.png", alt: "Akij Air" },
  { src: "/seo-landing/jk-group.png", alt: "JK Group" },
  { src: "/seo-landing/tax-vat-sheba.png", alt: "Tax VAT Sheba Bangladesh" },
];

const DIRECTORY_LOGOS = [
  { src: "/seo-landing/alternativeto.png", alt: "AlternativeTo" },
  { src: "/seo-landing/clutch.png", alt: "Clutch" },
  { src: "/seo-landing/softwaresuggest.png", alt: "SoftwareSuggest" },
];

export function TrustStrip() {
  return (
    <div className="strip">
      <div className="wrap strip-grid">
        {[
          { label: "Trusted by teams at", logos: CUSTOMER_LOGOS },
          { label: "Listed on", logos: DIRECTORY_LOGOS },
        ].map((col) => (
          <div className="strip-col" key={col.label}>
            <p>{col.label}</p>
            <div className="strip-logos">
              {col.logos.map((logo) => (
                <img
                  key={logo.alt}
                  className="lg"
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  decoding="async"
                  height={42}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const REVIEWS = [
  {
    quote:
      "TrackForce gives us clearer visibility into daily work and helps us manage team productivity more effectively.",
    initials: "MR",
    name: "Md Masiur Rahman",
    company: "Tax VAT Sheba Bangladesh",
  },
  {
    quote:
      "TrackForce makes employee activity easier to understand, helping us keep work focused and operations on track.",
    initials: "A",
    name: "Ali",
    company: "JK Spinning",
  },
  {
    quote:
      "With TrackForce, we get better work visibility and can manage team performance with greater clarity.",
    initials: "FA",
    name: "Fahad Ahmed",
    company: "Akij Air",
  },
];

export function ReviewQuotes() {
  return (
    <div className="g3">
      {REVIEWS.map((r) => (
        <article className="quote" key={r.name}>
          <div className="qstars" aria-label="Rated 5 out of 5">
            ★★★★★
          </div>
          <blockquote>
            <p>&quot;{r.quote}&quot;</p>
          </blockquote>
          <div className="qwho">
            <span className="av">{r.initials}</span>
            <div>
              <b>{r.name}</b>
              <span>{r.company}</span>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

export type Faq = { q: string; a: React.ReactNode; schemaA: string };

export function FaqList({ faqs }: { faqs: Faq[] }) {
  return (
    <div style={{ marginTop: 44 }}>
      {faqs.map((f, i) => (
        <details key={f.q} open={i === 0}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export function buildFaqJsonLd(faqs: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.schemaA },
    })),
  };
}

export function JsonLd({ data }: { data: object }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
